import React, { useState } from 'react'
import {
  Typography,
  MenuItem,
  Autocomplete,
  Chip,
  Checkbox,
  TextField,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'

const RenderTag = ({ tag, onDelete }) => {
  return (
    <Chip
      label={
        <Typography variant='body1KindaSmall' fontWeight={500}>
          {tag}
        </Typography>
      }
      deleteIcon={
        <CloseIcon
          style={{ fontSize: 20, color: colors.MidnightTeal400, margin: 0 }}
        />
      }
      onDelete={onDelete}
      sx={{
        height: 'auto',
        borderRadius: 1,
        backgroundColor: colors.MetalGrey50,
        gap: 1,
        padding: '2px 4px 2px 6px',
        '& .MuiChip-label': {
          padding: 0
        }
      }}
    />
  )
}

const ProfileSkillSelect = ({
  virtues,
  initialValue,
  onChangeCallback,
  ...otherProps
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const initialSkills =
    virtues
      .filter(v => initialValue.includes(v.id))
      ?.map(v => ({
        value: v.id,
        label: v.name
      })) || []
  const [selectedSkills, setSelectedSkills] = useState(initialSkills)
  const [isOpen, setIsOpen] = useState(false)

  const updateSkills = skills => {
    setSelectedSkills(skills)
    onChangeCallback(skills.map(v => v.value))
  }

  return (
    <Autocomplete
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      multiple
      name='skills'
      options={virtues.map(v => ({
        value: v.id,
        label: v.name
      }))}
      disableCloseOnSelect
      disableClearable
      disabled={selectedSkills?.length >= 5}
      renderOption={(props, option, { selected }) => (
        <MenuItem
          {...props}
          value={option.value}
          key={`filter-${option.value}`}
          sx={{
            '&.MuiAutocomplete-option': {
              padding: 3,
              overflow: 'hidden',
              '&.MuiAutocomplete-option[aria-selected="true"]': {
                backgroundColor: colors.White
              }
            }
          }}>
          <Checkbox checked={selected} />
          <Typography noWrap>{option.label}</Typography>
        </MenuItem>
      )}
      slotProps={{
        listbox: {
          sx: {
            paddingX: 4,
            paddingY: 6,
            borderRadius: 2
          }
        }
      }}
      renderValue={values => {
        return values?.map(skill => {
          return (
            <RenderTag
              key={skill?.value}
              tag={skill?.label}
              onDelete={() => {
                updateSkills(
                  selectedSkills.filter(item => item.value !== skill.value)
                )
              }}
            />
          )
        })
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={
            selectedSkills?.length === 0 ? t('profile.chooseSkills') : null
          }
          sx={{
            '&.MuiFormControl-root .MuiInputBase-root': {
              minHeight: 44,
              padding: '8px 35px 8px 16px',
              gap: 2,
              boxShadow: '1px 1px 10px 0px rgba(160, 160, 160, 0.05)',
              '& .MuiInputBase-input': {
                padding: 0
              }
            },
            '& .MuiInputBase-input::placeholder': {
              ...theme.typography.body1KindaSmall,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              opacity: 1
            }
          }}
        />
      )}
      onChange={(event, newValue) => {
        updateSkills(newValue)
      }}
      value={selectedSkills}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      fullWidth
      {...otherProps}
    />
  )
}

export default ProfileSkillSelect
